// +----------------------------------------------------------------------
// | 决策过程页 /decision/:id
// | 结构：问题 → 分析（追问/结论） → 因素 → 选项 → 做决定
// +----------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AIMessage from '../../components/AIMessage';
import FactorList from '../../components/FactorList';
import OptionCard from '../../components/OptionCard';
import { toast } from '../../components/Toast';
import {
  answerDecision,
  cancelDecision,
  chooseOption,
  getDecision,
  updateFactors,
} from '../../api/decision';
import useDecisionPoll from '../../hooks/useDecisionPoll';
import {
  COMPLEXITY_MAP,
  MODE_MAP,
  categoryLabel,
  statusLabel,
} from '../../lib/categoryMap';
import { weightToWidth } from '../../lib/format';
import './Decision.less';

export default function Decision() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [answer, setAnswer] = useState('');
  const [answering, setAnswering] = useState(false);
  const [factors, setFactors] = useState([]);
  const [recalculating, setRecalculating] = useState(false);
  const [chosenId, setChosenId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const applyData = useCallback((next) => {
    setData(next);
    if (next && Array.isArray(next.factors)) {
      setFactors(next.factors);
    }
    if (next && next.selected_option_id) {
      setChosenId(next.selected_option_id);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const result = await getDecision(id);
        if (mounted) applyData(result);
      } catch (e) {
        if (mounted) setLoadError(e.message || '打不开这个决定');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, applyData]);

  // AI 还在分析时轮询
  useDecisionPoll(id, {
    enabled: !!data && (data.next === 'analyzing' || data.status === 'analyzing'),
    onUpdate: applyData,
  });

  const handleAnswer = async (e) => {
    e.preventDefault();
    const text = answer.trim();
    if (!text || answering) return;

    setAnswering(true);
    try {
      const result = await answerDecision(id, text);
      applyData(result);
      setAnswer('');
    } catch (err) {
      toast(err.message || '没答上，再试一次', 'error');
    } finally {
      setAnswering(false);
    }
  };

  const handleFactorChange = (factor, weight) => {
    setFactors((prev) =>
      prev.map((it) => ((it.id && it.id === factor.id) || it.name === factor.name ? { ...it, weight } : it))
    );
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const payload = factors
        .filter((it) => it.id)
        .map((it) => ({ id: it.id, weight: Number(it.weight) }));
      if (!payload.length) {
        toast('因素还没保存好，先聊两句');
        return;
      }
      const result = await updateFactors(id, payload);
      applyData(result);
      toast('按你的权重重新算过了', 'success');
    } catch (e) {
      toast(e.message || '没算成，再试一次', 'error');
    } finally {
      setRecalculating(false);
    }
  };

  const handleChoose = async () => {
    if (!chosenId) {
      toast('先选一个吧');
      return;
    }
    setSubmitting(true);
    try {
      await chooseOption(id, chosenId);
      toast('记下了', 'success');
      navigate(`/decision/${id}/result`);
    } catch (e) {
      toast(e.message || '没记上，再试一次', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelDecision(id);
      toast('这个就先放着吧');
      navigate('/history');
    } catch (e) {
      toast(e.message || '取消失败', 'error');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="page center decision-loading">
        <span className="dots">
          <i />
          <i />
          <i />
        </span>
        <p className="muted">正在打开…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page">
        <div className="card center decision-error">
          <p>{loadError}</p>
          <div className="decision-error-actions">
            <Link to="/" className="btn btn-primary btn-sm">
              回首页
            </Link>
            <Link to="/history" className="btn btn-ghost btn-sm">
              我的决定
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const understanding = data.understanding || {};
  const options = data.options || [];
  const messages = data.messages || [];
  const isAnalyzing = data.next === 'analyzing';
  const isAsking = data.next === 'ask';
  const isDecided = data.status === 'decided';
  const complexity = COMPLEXITY_MAP[data.complexity] || null;
  const mode = MODE_MAP[data.mode] || null;

  // Phase 8：这次分析参考了哪些外部信息（时间上下文、天气等）
  const externalSources = Array.isArray(data.external_sources) ? data.external_sources : [];
  const conclusion = data.ai_summary || data.conclusion || '';
  const confidence = data.confidence;
  // Phase 4：AI 会指出「还缺哪些关键信息」，想到什么就补一句，判断会更准
  const missingInfo = Array.isArray(data.missing_information) ? data.missing_information : [];

  return (
    <div className="page decision">
      <header className="decision-head">
        <h1 className="decision-question">{data.title || data.question}</h1>
        <div className="decision-tags">
          {data.category && <span className="tag">{categoryLabel(data.category)}</span>}
          {complexity && <span className="tag tag-plain">{complexity.label}</span>}
          {mode && <span className="tag tag-plain">{mode.label}</span>}
          <span className="tag tag-plain">{statusLabel(data.status)}</span>
        </div>
      </header>

      {data.risk_flag === 1 && (
        <div className="card decision-risk">
          这件事涉及较高风险，我只能帮你把信息和考虑的因素整理清楚，不能替你拍板，也不该替代专业人士。
        </div>
      )}

      {Number(data.memory_used) > 0 && (
        <div className="decision-memory-hint muted">
          这次参考了你之前的 {data.memory_used} 条记忆 ·{' '}
          <Link to="/memories">看看记了什么</Link>
        </div>
      )}

      {externalSources.length > 0 && (
        <div className="decision-external-hint muted">
          这次还看了实时信息 ·{' '}
          {externalSources.map((item) => item.label || item.name).filter(Boolean).join('、')}
          <span className="decision-external-note">（只在这一次分析里用过，不会存下来）</span>
        </div>
      )}

      {(understanding.core_conflict || understanding.question) && (
        <section className="card decision-conflict">
          <div className="decision-section-title">我看到的纠结</div>
          {understanding.core_conflict ? (
            <p>{understanding.core_conflict}</p>
          ) : (
            <p>{understanding.question}</p>
          )}
        </section>
      )}

      <section className="decision-thread">
        {messages.map((msg, index) => (
          <AIMessage key={msg.id || `msg-${index}`} role={msg.role === 'user' ? 'user' : 'ai'}>
            {msg.content}
          </AIMessage>
        ))}

        {data.ai_message && !isAsking && !isAnalyzing && (
          <AIMessage role="ai">{data.ai_message}</AIMessage>
        )}

        {isAnalyzing && <AIMessage role="ai" loading />}
      </section>

      {isAsking && data.ai_message && (
        <section className="card decision-ask">
          <AIMessage role="ai">{data.ai_message}</AIMessage>
          <form onSubmit={handleAnswer} className="decision-answer">
            <textarea
              className="input"
              rows={2}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="说说看…"
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={answering || !answer.trim()}
            >
              {answering ? '想想…' : '继续'}
            </button>
          </form>
          <p className="muted decision-ask-note">
            答完这个，我接着往下想。只问最关键的，不会跟你没完没了地聊。
          </p>
        </section>
      )}

      {factors.length > 0 && (
        <section className="card">
          <div className="decision-section-head">
            <div className="decision-section-title">你现在更在乎</div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleRecalculate}
              disabled={recalculating}
            >
              {recalculating ? '算着…' : '按我的权重重算'}
            </button>
          </div>
          <FactorList factors={factors} onChange={handleFactorChange} />
          <p className="muted decision-factor-note">
            左右拖一下，把更在意的因素往右拉。
          </p>
        </section>
      )}

      {options.length > 0 && (
        <section className="decision-options">
          <div className="decision-section-title">你的选项</div>
          {options.map((option, index) => (
            <OptionCard
              key={option.id || option.name}
              option={option}
              index={index}
              selectable={!isDecided}
              selected={chosenId === option.id}
              onSelect={(opt) => setChosenId(opt.id)}
              showPercent={confidence !== undefined && confidence !== null && !isAsking}
            />
          ))}
        </section>
      )}

      {!isDecided && missingInfo.length > 0 && (
        <section className="card decision-missing">
          <div className="decision-section-title">还差这些信息</div>
          <div className="decision-missing-list">
            {missingInfo.map((item) => (
              <span key={item} className="decision-missing-tag">{item}</span>
            ))}
          </div>
          <p className="muted decision-missing-note">
            这几条最影响判断。想到了就接着说一句，我再重新掂量。
          </p>
        </section>
      )}

      {confidence !== undefined && confidence !== null && !isAsking && (
        <section className="card decision-confidence">
          <div className="decision-section-title">我当前的判断</div>
          {conclusion ? <p className="decision-conclusion">{conclusion}</p> : null}
          <div className="decision-confidence-bar">
            <span style={{ width: `${weightToWidth(confidence)}%` }} />
          </div>
          <p className="muted decision-confidence-note">
            这只是「基于你现在说的这些」的倾向参考，不是概率，也不是替你做决定。
          </p>
        </section>
      )}

      <section className="decision-actions">
        {isDecided ? (
          <>
            <div className="decision-decided">
              已经定了：{data.selected_option ? data.selected_option.name : '已记录'}
            </div>
            <Link to={`/decision/${id}/result`} className="btn btn-primary btn-block">
              过阵子再说说结果
            </Link>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={handleChoose}
              disabled={submitting || !chosenId}
            >
              {submitting ? '记录中…' : '就它了'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-block decision-cancel"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? '处理中…' : '我再想想，先放着'}
            </button>
          </>
        )}
      </section>
    </div>
  );
}
